import { Fragment, useEffect, useRef } from "react";
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from "@headlessui/react";
import { QuestionMarkCircleIcon } from "@heroicons/react/24/outline";
import { ExclamationCircleIcon } from "@heroicons/react/24/outline";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { AlertLevel } from "./AlertMessage";

export interface Props {
	open: boolean;
	type: AlertLevel;
	title: string;
	message: string;
	confirmLabel: string;
	cancelLabel?: string;
	onConfirm: () => void;
	onCancel?: () => void;
}

// From https://tailwindui.com/components/application-ui/overlays/modals, Simple alert
export default function ModalAlert(props: Props) {
	const cancelButtonRef = useRef(null);

	function handleConfirm() {
		props.onConfirm();
	}

	function handleCancel() {
		props.onCancel && props.onCancel();
	}

	useEffect(() => {
		const keyDownHandler = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				event.preventDefault();
				handleCancel();
			} else if (event.key === "Enter") {
				event.preventDefault();
				handleConfirm();
			}
		};

		if (!props.open) {
			document.removeEventListener("keydown", keyDownHandler);
			return;
		}

		document.addEventListener("keydown", keyDownHandler);

		return () => {
			document.removeEventListener("keydown", keyDownHandler);
		};
	}, [props.open]);

	return (
		<Transition show={props.open} as={Fragment}>
			<Dialog
				as="div"
				className="relative z-10"
				initialFocus={cancelButtonRef}
				onClose={() => {}}>
				<TransitionChild
					as={Fragment}
					enter="ease-out duration-300"
					enterFrom="opacity-0"
					enterTo="opacity-100"
					leave="ease-in duration-200"
					leaveFrom="opacity-100"
					leaveTo="opacity-0">
					<div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
				</TransitionChild>

				<div className="fixed inset-0 z-10 overflow-y-auto">
					<div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
						<TransitionChild
							as={Fragment}
							enter="ease-out duration-300"
							enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
							enterTo="opacity-100 translate-y-0 sm:scale-100"
							leave="ease-in duration-200"
							leaveFrom="opacity-100 translate-y-0 sm:scale-100"
							leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
							<DialogPanel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
								<div className="sm:flex sm:items-start">
									{props.type === AlertLevel.Error && (
										<div className="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
											<ExclamationTriangleIcon
												className="h-6 w-6 text-red-600"
												aria-hidden="true"
											/>
										</div>
									)}
									{props.type === AlertLevel.Warning && (
										<div className="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
											<ExclamationCircleIcon
												className="h-6 w-6 text-yellow-800"
												aria-hidden="true"
											/>
										</div>
									)}
									{props.type === AlertLevel.Info && (
										<div className="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-green-100 sm:mx-0 sm:h-10 sm:w-10">
											<QuestionMarkCircleIcon
												className="h-6 w-6 text-green-600"
												aria-hidden="true"
											/>
										</div>
									)}
									<div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
										<DialogTitle
											as="h3"
											className="text-base font-semibold leading-6 text-gray-900">
											{props.title}
										</DialogTitle>
										<div className="mt-2">
											<p className="text-sm text-gray-500">{props.message}</p>
										</div>
									</div>
								</div>
								<div className="mt-5 sm:mt-4 sm:flex sm:justify-end">
									<button
										type="button"
										id="confirm-button"
										className={`inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm sm:mr-3 sm:w-auto
											${((type) => {
												switch (type) {
													default:
													case AlertLevel.Error:
														return "bg-red-600 hover:bg-red-500 ";
													case AlertLevel.Warning:
														return "bg-yellow-600 hover:bg-yellow-500";
													case AlertLevel.Info:
														return "bg-green-600 hover:bg-green-500";
												}
											})(props.type)}`}
										onClick={() => handleConfirm()}>
										{props.confirmLabel}
									</button>
									{props.onCancel && (
										<button
											type="button"
											id="cancel-button"
											className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
											onClick={() => handleCancel()}
											ref={cancelButtonRef}>
											{props.cancelLabel}
										</button>
									)}
								</div>
							</DialogPanel>
						</TransitionChild>
					</div>
				</div>
			</Dialog>
		</Transition>
	);
}
